(() => {
  if (customElements.get('jd-showroom')) return;
  const playingRooms = new Set();
  const stop = room => {
    if (!room?.activeMedia) return;
    const {node, button, resize} = room.activeMedia;
    room.activeMedia = null;
    playingRooms.delete(room);
    resize?.disconnect();
    if (node.tagName === 'VIDEO') node.pause();
    button.parentElement?.removeAttribute('data-playing');
    node.remove();
    button.hidden = false;
  };
  const stopAll = () => [...playingRooms].forEach(stop);
  document.addEventListener('visibilitychange', () => { if (document.hidden) stopAll(); });
  addEventListener('pagehide', stopAll);
  customElements.define('jd-showroom', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('.jd-room-track');
      if (!this.track) return;
      this.cards = [...this.track.children].filter(card => !card.hidden);
      if (!this.cards.length) { this.hidden = true; return; }
      this.abort = new AbortController();
      const opts = {signal:this.abort.signal};
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.queuePreview = () => {
        clearTimeout(this.previewTimer);
        if (!this.inFocus || this.dragging || document.hidden || motion.matches) return;
        const card = this.cards[this.index];
        const button = card?.querySelector('.jd-room-play');
        if (!button || button.hidden || this.activeMedia?.card === card) return;
        this.previewTimer = setTimeout(() => {
          if (card === this.cards[this.index] && this.inFocus && !document.hidden && !motion.matches) this.play(button, true);
        }, 220);
      };
      this.focusObserver = new IntersectionObserver(([entry]) => {
        this.inFocus = entry.isIntersecting;
        if (!this.inFocus && this.activeMedia?.room === this) stop(this);
        this.queuePreview();
      }, {rootMargin:'0px'});
      this.focusObserver.observe(this.track);
      document.addEventListener('visibilitychange',this.queuePreview,opts);
      window.addEventListener('pageshow',this.queuePreview,opts);
      motion.addEventListener('change',() => {
        if (motion.matches && this.activeMedia?.room === this) stop(this);
        this.queuePreview();
      },opts);
      this.index = -1;
      this.dots = document.createElement('span');
      this.dots.className = 'jd-room-dots';
      this.dots.setAttribute('aria-hidden','true');
      this.cards.forEach(() => this.dots.append(document.createElement('i')));
      this.querySelector('output')?.after(this.dots);
      this.onScroll = () => {
        if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.update(); });
      };
      this.track.addEventListener('scroll', this.onScroll, {...opts, passive:true});
      this.resize = new ResizeObserver(this.onScroll);
      this.resize.observe(this.track);
      this.observer = new IntersectionObserver(([entry]) => {
        this.onScreen = entry.isIntersecting;
        this.queuePreview();
        if (!this.onScreen && this.activeMedia?.room === this) stop(this);
      });
      this.observer.observe(this.track);
      this.addEventListener('keydown', e => {
        if (e.target !== this.track || !['ArrowLeft','ArrowRight'].includes(e.key)) return;
        e.preventDefault(); this.move(this.index + (e.key === 'ArrowRight' ? 1 : -1));
      }, opts);
      this.track.addEventListener('dragstart', e => e.preventDefault(), opts);
      this.track.addEventListener('pointerdown', e => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('input,select,textarea')) return;
        this.pointer = {id:e.pointerId,x:e.clientX,y:e.clientY,left:this.track.scrollLeft};
      }, opts);
      window.addEventListener('pointermove', e => {
        const p = this.pointer;
        if (!p || e.pointerId !== p.id) return;
        const dx = e.clientX-p.x;
        if (!this.dragging) {
          if (Math.abs(dx)<8 || Math.abs(dx)<Math.abs(e.clientY-p.y)) return;
          this.dragging = true;
          this.track.setPointerCapture(p.id);
          this.track.classList.add('is-dragging');
          clearTimeout(this.previewTimer);
          if (this.activeMedia?.room === this) stop(this);
        }
        e.preventDefault();
        this.track.scrollLeft = p.left-dx;
      }, {...opts,passive:false});
      this.finishDrag = e => {
        if (!this.pointer || e.pointerId !== this.pointer.id) return;
        const dragged = this.dragging;
        this.pointer = null;
        this.dragging = false;
        this.track.classList.remove('is-dragging');
        if (this.track.hasPointerCapture(e.pointerId)) this.track.releasePointerCapture(e.pointerId);
        if (dragged) {
          this.suppressClickUntil = performance.now()+350;
          const distances = this.cards.map(card => Math.abs(this.offset(card)));
          this.move(distances.indexOf(Math.min(...distances)));
          this.queuePreview();
        }
      };
      window.addEventListener('pointerup', this.finishDrag, opts);
      window.addEventListener('pointercancel', this.finishDrag, opts);
      this.addEventListener('click', e => {
        if (performance.now() < (this.suppressClickUntil || 0)) {
          e.preventDefault(); e.stopImmediatePropagation(); return;
        }
        const sideCard = e.target.closest('.jd-room-slide');
        if (sideCard && sideCard !== this.cards[this.index]) {
          e.preventDefault(); e.stopImmediatePropagation();
          this.move(this.cards.indexOf(sideCard)); return;
        }
      }, {...opts,capture:true});
      this.addEventListener('click', e => {
        const step = e.target.closest('[data-jd-room-step]');
        if (step) { e.stopPropagation(); this.move(this.index + Number(step.dataset.jdRoomStep)); return; }
        const button = e.target.closest('.jd-room-play');
        if (button) { e.stopPropagation(); this.play(button); }
      },opts);
      this.cards.forEach(card => {
        const button = card.querySelector('.jd-room-play');
        if (button) button.hidden = !(card.querySelector('template') || this.design(card));
      });
      if (this.cards.length > 2) this.track.scrollLeft += this.offset(this.cards[Math.min(3,this.cards.length-2)]);
      this.update();
    }
    edgeCard(last) {
      const cards = [...this.track.children].filter(card => !card.hidden);
      return cards[last ? cards.length - 1 : 0];
    }
    design(card) {
      return window.JordigiApproved?.products?.find(p => p.id === card.dataset.key);
    }
    move(index) {
      const card = this.cards[(index + this.cards.length) % this.cards.length];
      const current = this.cards[this.index];
      const align = element => element.offsetLeft + element.offsetWidth/2 - this.track.clientWidth/2;
      // At the seam, put both neighbours in the rail before starting the transition.
      if (this.cards.length > 2 && (card === this.edgeCard(false) || card === this.edgeCard(true))) {
        this.track.style.scrollSnapType = 'none';
        if (index < this.index && current === this.edgeCard(false)) {
          this.track.prepend(card);
          this.track.prepend(this.edgeCard(true));
        } else if (index > this.index && current === this.edgeCard(true)) {
          this.track.append(card);
          this.track.append(this.edgeCard(false));
        } else if (card === this.edgeCard(false)) this.track.prepend(this.edgeCard(true));
        else this.track.append(this.edgeCard(false));
        this.track.scrollLeft = align(current);
        this.track.style.scrollSnapType = '';
      }
      this.track.scrollTo({left:align(card),behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'instant':'smooth'});
    }
    offset(card) {
      return card.offsetLeft + card.offsetWidth/2 - this.track.scrollLeft - this.track.clientWidth/2;
    }
    update() {
      const distances = this.cards.map(card => Math.abs(this.offset(card)));
      const index = distances.indexOf(Math.min(...distances));
      const current = this.cards[index];
      // Rotate existing cards at the ends. Keep the same visual anchor and media node.
      if (!this.dragging && this.cards.length > 2 && distances[index] < 2) {
        const before = current.offsetLeft;
        if (current === this.edgeCard(false)) this.track.prepend(this.edgeCard(true));
        else if (current === this.edgeCard(true)) this.track.append(this.edgeCard(false));
        const shift = current.offsetLeft - before;
        if (shift) this.track.scrollLeft = current.offsetLeft + current.offsetWidth/2 - this.track.clientWidth/2;
      }
      if (index === this.index && this.lastFirst === this.edgeCard(false)) return;
      this.index = index;
      this.lastFirst = this.edgeCard(false);
      for (const card of this.cards) {
        card.setAttribute('aria-current',String(card === current));
        card.style.setProperty('--side',Math.sign(current.offsetLeft-card.offsetLeft));
      }
      if (this.activeMedia?.room === this && this.activeMedia.card !== current) stop(this);
      this.queuePreview();
      const output = this.querySelector('output');
      if (output) output.textContent = (index+1)+' / '+this.cards.length;
      Array.from(this.dots.children).forEach((dot,i) => dot.classList.toggle('on',i===index));
    }
    play(button, automatic = false) {
      const card = button.closest('.jd-room-slide');
      if (card !== this.cards[this.index]) { this.move(this.cards.indexOf(card)); return; }
      if (document.hidden || !this.onScreen) return;
      stop(this);
      const template = card.querySelector('template');
      const design = this.design(card);
      if (!template && !design) return;
      const node = template ? template.content.firstElementChild.cloneNode(true) : document.createElement('iframe');
      this.activeMedia = {room:this,card,node,button};
      playingRooms.add(this);
      if (!template) {
        node.title = design.name;
        node.setAttribute('sandbox','allow-scripts');
        const html = window.JordigiApproved.documentFor(design,design.defaults,true);
        const style = '<style>html,body{background:transparent!important}#canvas,#stage{background:transparent!important}#veil{display:none!important}</style>';
        node.srcdoc = html.includes('</head>') ? html.replace('</head>',style+'</head>') : html.replace('<body>',style+'<body>');
        const fit = () => {
          const media = button.parentElement;
          const scale = Math.min(media.clientWidth/900,media.clientHeight/650);
          node.style.cssText = 'position:absolute!important;left:50%!important;top:50%!important;width:900px!important;height:650px!important;max-width:none!important;border:0!important;transform:translate(-50%,-50%) scale('+scale+');transform-origin:center;background:transparent';
        };
        const resize = new ResizeObserver(fit);
        this.activeMedia.resize = resize;
        resize.observe(button.parentElement);
        fit();
        node.addEventListener('load', () => {
          if (this.activeMedia?.node !== node) return;
          for (const action of ['update','play']) node.contentWindow.postMessage({type:'jd-paint-command',action,values:design.defaults},'*');
        },{once:true});
      }
      button.parentElement.setAttribute('data-playing','');
      node.addEventListener('ended',() => {if (this.activeMedia?.node === node) button.hidden = false;});
      node.addEventListener('error',() => {if (this.activeMedia?.node === node) stop(this);},{once:true});
      if (template) { node.playsInline = true; node.loop = false; if (automatic) { node.muted = true; node.defaultMuted = true; } }
      button.before(node); button.hidden = true;
      if (template) node.play().catch(() => { if (this.activeMedia?.node === node) stop(this); });
    }
    disconnectedCallback() {
      if (this.activeMedia?.room === this) stop(this);
      clearTimeout(this.previewTimer); this.focusObserver?.disconnect();
      this.dots?.remove();
      this.abort?.abort(); this.abort = null; this.observer?.disconnect(); this.resize?.disconnect();
      cancelAnimationFrame(this.raf); this.raf = 0;
    }
  });
})();


